import { Box, BoxProps } from "@chakra-ui/react";
import { createContext } from "react";

import { RanaErrors } from "../components/rana-errors";
import { FormDerivationRulesFor } from "../rana-form-derived-state";

import { RanaFormCancelButton } from "./rana-form-input-components/rana-form-cancel-button";
import { RanaFormDropdown } from "./rana-form-input-components/rana-form-dropdown";
import { RanaFormGeocoderInput } from "./rana-form-input-components/rana-form-geocoder-input";
import { RanaFormInput } from "./rana-form-input-components/rana-form-input";
import { RanaFormSubmitButton } from "./rana-form-input-components/rana-form-submit-button";
import { RanaFormTextarea } from "./rana-form-input-components/rana-form-textarea";
import {
  AsyncSubmitFunction,
  FormErrors,
  FormValuesExtend,
  SubmitFunction,
  ValidationFunction,
  useRanaForm,
} from "./use-rana-form";

export interface RanaFormProps<FormValues> {
  initialValues?: FormValues;
  required?: string[];
  validate?: ValidationFunction<FormValues>;
  doSubmit?: SubmitFunction<FormValues> | AsyncSubmitFunction<FormValues>;
  emptyOnSubmit?: boolean;
  derivationRules?: FormDerivationRulesFor<FormValues>;
}

interface RanaFormContext<FormValues> {
  values: FormValues;
  errors: FormErrors;
  required: string[];
  addRequired: (requiredField: string) => void;
  submitting: boolean;
  setPartialValues: (values: FormValues) => void;
  onCancel: () => void;
  onSubmit: () => void;
  geoProps: {
    bbox: [number, number, number, number];
    setBbox: (bbox: [number, number, number, number]) => void;
    filterGeoByMapBbox: boolean;
    setFilterGeoByMapBbox: (filterGeoByMapBbox: boolean) => void;
  };
}

export const RanaFormContext = createContext<RanaFormContext<FormValuesExtend>>({
  values: {},
  errors: {},
  required: [],
  submitting: false,
  addRequired: () => undefined,
  setPartialValues: () => undefined,
  onCancel: () => undefined,
  onSubmit: () => undefined,
  geoProps: {
    bbox: [-180, -90, 180, 90],
    setBbox: () => undefined,
    filterGeoByMapBbox: false,
    setFilterGeoByMapBbox: () => undefined,
  },
});

export function RanaForm<FormValues extends FormValuesExtend>({
  initialValues,
  validate,
  doSubmit,
  emptyOnSubmit,
  derivationRules,
  children,
  ...rest
}: RanaFormProps<FormValues> & BoxProps) {
  const {
    values,
    errors,
    required,
    addRequired,
    submitting,
    setPartialValues,
    onCancel,
    handleSubmit,
    bbox,
    setBbox,
    filterGeoByMapBbox,
    setFilterGeoByMapBbox,
    otherErrors,
  } = useRanaForm({ initialValues, validate, doSubmit, emptyOnSubmit, derivationRules });

  return (
    <Box {...rest}>
      <RanaFormContext.Provider
        value={{
          values,
          errors,
          required,
          addRequired,
          submitting,
          setPartialValues,
          onCancel,
          onSubmit: handleSubmit,
          geoProps: {
            bbox,
            setBbox,
            filterGeoByMapBbox,
            setFilterGeoByMapBbox,
          },
        }}
      >
        {children}
        <RanaErrors errors={otherErrors} />
      </RanaFormContext.Provider>
    </Box>
  );
}

// Add subcomponents to the main RanaForm component as they cannot be used outside
// RanaForm.
RanaForm.Dropdown = RanaFormDropdown;
RanaForm.Input = RanaFormInput;
RanaForm.Textarea = RanaFormTextarea;
RanaForm.SubmitButton = RanaFormSubmitButton;
RanaForm.CancelButton = RanaFormCancelButton;
RanaForm.GeocoderInput = RanaFormGeocoderInput;
